import ASearch from '@/components/A/ASearch/ASearch'
import ASelect from '@/components/A/ASelect/ASelect'
import AIcon from '@/components/A/AIcon/AIcon'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/DropdownMenu/DropdownMenu'
import { commonUtils } from '@/helpers/commonUtils'
import { DASHBOARD_CONTENT_CONTAINER_ID } from '@/router/pageConst'
import { useSize } from 'ahooks'
import cls from 'classnames'
import { useTranslation } from 'react-i18next'
import styles from './ProjectFilter.module.less'

const getDashboardContentContainer = () => (
  typeof document === 'undefined'
    ? null
    : document.getElementById(DASHBOARD_CONTENT_CONTAINER_ID)
)

interface SortFilterProps {
  onSortChange: (value: 'score_asc' | 'score_desc') => void
}

interface SearchFilterProps {
  onSearchChange: (value: string) => void
}

function ProjectSortFilter(props: SortFilterProps) {
  const { onSortChange } = props
  const { t } = useTranslation()
  const ORDER_OPTIONS = [
    { value: 'score_asc', label: t('dashboard.projectPage.sortOption1') },
    { value: 'score_desc', label: t('dashboard.projectPage.sortOption2') },
  ]
  return <div className={styles.order}>
    <ASelect
      testId='project-sort-select'
      className={styles.selector}
      placeholder={t('dashboard.projectPage.sortPlaceholder')}
      options={ORDER_OPTIONS}
      onChange={(value) => {
        onSortChange(value)
      }}
      allowClear
    />
  </div>
}

interface ProjectFilterSortProps {
  normType: 1 | 2 | undefined
  order: 'score_asc' | 'score_desc' | undefined
  onNormTypeChange: (value: 1 | 2 | undefined) => void
  onSortChange: (value: 'score_asc' | 'score_desc' | undefined) => void
}

function ProjectFilterSort(props: ProjectFilterSortProps) {
  const { normType, order, onNormTypeChange, onSortChange } = props
  const { t, i18n } = useTranslation()
  const dashboardContentSize = useSize(getDashboardContentContainer)
  // useSize triggers updates; offsetWidth matches the container's full rendered width.
  const dashboardContentWidth = dashboardContentSize === undefined
    ? undefined
    : getDashboardContentContainer()?.offsetWidth
  const isJaLocale = (i18n.resolvedLanguage || i18n.language || '').toLowerCase().startsWith('ja')
  const compact = isJaLocale && dashboardContentWidth !== undefined && dashboardContentWidth < 1350
  const normTypeOptions = [
    { value: 1 as const, label: t('dashboard.projectCreatePage.targetMarketNorm') },
    { value: 2 as const, label: t('dashboard.projectCreatePage.brandNorm') },
  ]
  const orderOptions = [
    { value: 'score_desc' as const, label: t('dashboard.projectPage.sortOption2') },
    { value: 'score_asc' as const, label: t('dashboard.projectPage.sortOption1') },
  ]

  const highlighted = normType !== undefined || order !== undefined
  const triggerIcon = highlighted
    ? commonUtils.getImagePath('projectPage/project-list-filter-solid.svg')
    : commonUtils.getImagePath('projectPage/project-list-filter.svg')

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid='project-filter-sort-btn'
          className={cls(
            styles.filterTrigger,
            highlighted && styles.filterTriggerActive,
            compact && styles.filterTriggerCompact
          )}
          type='button'
        >
          <img src={triggerIcon} alt='' className={styles.filterIcon} />
          <span className={styles.filterTriggerText}>{t('dashboard.projectPage.filterSort')}</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        sideOffset={8}
        align='start'
        className={styles.filterContent}
        style={{ minWidth: 228 }}
      >
        <div className={styles.filterSectionTitle}>{t('dashboard.projectPage.filterBenchmarkTitle')}</div>
        {normTypeOptions.map((option) => (
          <DropdownMenuItem
            key={option.value}
            data-testid={`project-norm-option-${option.value}`}
            className={cls(styles.filterItem, normType === option.value && styles.filterItemActive)}
            onSelect={(event) => {
              event.preventDefault()
              onNormTypeChange(normType === option.value ? undefined : option.value)
            }}
          >
            <span className={styles.filterItemLabel}>{option.label}</span>
            <span className={styles.filterItemCheckSlot}>
              {normType === option.value ? <AIcon className={styles.filterItemCheck} type='icon-line-global_check' /> : null}
            </span>
          </DropdownMenuItem>
        ))}
        <div className={cls(styles.filterSectionTitle, styles.filterSectionGap)}>{t('dashboard.projectPage.sortTitle')}</div>
        {orderOptions.map((option) => (
          <DropdownMenuItem
            key={option.value}
            data-testid={`project-sort-option-${option.value}`}
            className={cls(styles.filterItem, order === option.value && styles.filterItemActive)}
            onSelect={(event) => {
              event.preventDefault()
              onSortChange(order === option.value ? undefined : option.value)
            }}
          >
            <span className={styles.filterItemLabel}>{option.label}</span>
            <span className={styles.filterItemCheckSlot}>
              {order === option.value ? <AIcon className={styles.filterItemCheck} type='icon-line-global_check' /> : null}
            </span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function ProjectSearchFilter(props: SearchFilterProps) {
  const { onSearchChange } = props
  const { t } = useTranslation()
  return <div className={styles.order}>
    <ASearch
      testId='project-search-input'
      className={styles.searchInput}
      placeholder={t('dashboard.projectPage.searchPlaceholder')}
      allowClear
      onSearch={(value) => {
        const keyword = value ? `${value}` : ''
        onSearchChange(keyword)
      }}
    ></ASearch>
  </div>
}

export { ProjectFilterSort, ProjectSortFilter, ProjectSearchFilter }
